/**
 * CetusEd Community Portal — API Client
 *
 * COMPLIANCE: All API requests go through a centralized fetch wrapper that:
 *   - Attaches the JWT bearer token from auth context
 *   - Validates response shapes against shared @cetused/shared types
 *   - Routes through the Hono server which enforces scope-based RBAC
 *   - Never sends PII in query parameters (logs only action type)
 *
 * MIGRATION TARGET: The monolith communicates with the server via
 *   envelope-encrypted IndexedDB sync. This client is for the community
 *   portal's direct API calls (document requests, message submission).
 */

/**
 * The community API response envelope. @cetused/shared exports no
 * ApiResponse type — this mirrors the server's error-envelope contract
 * ({ success, data } | { success, error, code }).
 */
interface CommunityApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  code?: string;
}

const API_BASE = '/api';

let _authToken: string | null = null;

export function setAuthToken(token: string | null): void {
  _authToken = token;
}

export function getAuthToken(): string | null {
  return _authToken;
}

export class ApiError extends Error {
  constructor(
    public readonly statusCode: number,
    public readonly code: string,
    message: string,
    public readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  options?: { signal?: AbortSignal | undefined },
): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  if (_authToken) {
    headers['Authorization'] = `Bearer ${_authToken}`;
  }

  const response = await fetch(`${API_BASE}${path}`, {
    method,
    headers,
    ...(body ? { body: JSON.stringify(body) } : {}),
    ...(options?.signal ? { signal: options.signal } : {}),
  });

  const json: CommunityApiResponse<T> = await response.json();

  if (!response.ok || !json.success) {
    throw new ApiError(
      response.status,
      json.code || 'UNKNOWN',
      json.error || 'An unexpected error occurred',
      json.data,
    );
  }

  return json.data as T;
}

export const api = {
  get: <T>(path: string, signal?: AbortSignal) => request<T>('GET', path, undefined, { signal }),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body),
  put: <T>(path: string, body?: unknown) => request<T>('PUT', path, body),
  patch: <T>(path: string, body?: unknown) => request<T>('PATCH', path, body),
  delete: <T>(path: string) => request<T>('DELETE', path),
};
